// src/components/support/OperatorStatusManager.jsx
//
// تخته‌ی «وضعیت اپراتورها» — فقط خواندنی.
//
// نسخه‌ی پیشین این صفحه کاملاً ساختگی بود: نام‌های واقعی کارکنان به‌صورت هاردکد، کلیدهای
// فعال/غیرفعال که هیچ درخواستی نمی‌فرستادند و یک مودالِ «جابجایی شیفت» که فقط state محلی را
// عوض می‌کرد. برای ادمین این یعنی داده‌ی جعلی و کنترل‌های دروغین. چون در بک‌اند نه اندپوینت
// تغییر وضعیت وجود دارد و نه انتقال شیفت، آن کنترل‌ها حذف شده‌اند و صفحه فقط همان چیزی را
// نشان می‌دهد که سرور واقعاً می‌داند: بار لحظه‌ای هر اپراتور و در شیفت بودنش.
import React, {useMemo, useState} from "react";
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Empty,
    Grid,
    Progress,
    Row,
    Select,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import {ReloadOutlined, TeamOutlined} from "@ant-design/icons";
import {useLiveOperators, useLiveSnapshot} from "./operator-status-manager-query";

const {Text} = Typography;
const {useBreakpoint} = Grid;

const enToFaDigits = (v) =>
    String(v ?? "").replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]);

const OperatorStatusManager = () => {
    const screens = useBreakpoint();
    const isDesktop = !!screens.md;

    const {
        data: operatorsData,
        isLoading,
        isError,
        isFetching,
        refetch,
    } = useLiveOperators();

    // سقف هم‌زمانی از snapshot می‌آید (قرارداد live-operators خودش آن را ندارد).
    const {data: snapshot} = useLiveSnapshot();
    const maxPerOperator = snapshot?.maxChatsPerOperator ?? 0;

    const [filterMode, setFilterMode] = useState("ALL"); // ALL | ON_SHIFT | OFF_SHIFT

    const rows = useMemo(() => {
        const list = Array.isArray(operatorsData) ? operatorsData : [];
        return [...list].sort((a, b) => {
            if (!!b.onShift !== !!a.onShift) return b.onShift ? 1 : -1;
            return (b.activeChats ?? 0) - (a.activeChats ?? 0);
        });
    }, [operatorsData]);

    const filteredRows = useMemo(() => {
        if (filterMode === "ON_SHIFT") return rows.filter((r) => r.onShift);
        if (filterMode === "OFF_SHIFT") return rows.filter((r) => !r.onShift);
        return rows;
    }, [rows, filterMode]);

    const renderLoad = (record) => {
        const active = record.activeChats ?? 0;
        const percent = maxPerOperator
            ? Math.min(100, (active / maxPerOperator) * 100)
            : 0;
        const full = maxPerOperator > 0 && active >= maxPerOperator;
        return (
            <Space direction="vertical" size={2} style={{width: "100%"}}>
                <Text style={{fontSize: 12}}>
                    {enToFaDigits(active)}
                    {maxPerOperator ? ` / ${enToFaDigits(maxPerOperator)}` : ""}
                </Text>
                {maxPerOperator ? (
                    <Progress
                        percent={percent}
                        showInfo={false}
                        size="small"
                        status={full ? "exception" : "normal"}
                    />
                ) : null}
            </Space>
        );
    };

    const columns = [
        {
            title: "اپراتور",
            dataIndex: "fullName",
            width: 240,
            render: (v, r) => (
                <Space>
                    <Badge
                        status={r.onShift ? "success" : "default"}
                        title={r.onShift ? "در شیفت" : "خارج از شیفت"}
                    />
                    <Text strong>{v}</Text>
                </Space>
            ),
        },
        {
            title: "شیفت",
            dataIndex: "onShift",
            width: 140,
            render: (onShift) =>
                onShift ? (
                    <Tag color="green">در شیفت</Tag>
                ) : (
                    <Tag>خارج از شیفت</Tag>
                ),
        },
        {
            title: "گفت‌وگوهای فعال",
            key: "activeChats",
            width: 200,
            render: (_, r) => renderLoad(r),
        },
        {
            title: "در صف این اپراتور",
            dataIndex: "queuedChats",
            width: 150,
            align: "center",
            render: (v) => enToFaDigits(v ?? 0),
        },
        {
            title: "خوانده‌نشده",
            dataIndex: "unreadChats",
            width: 140,
            align: "center",
            render: (v) =>
                (v ?? 0) > 0 ? (
                    <Tag color="red" className="rounded-full">
                        {enToFaDigits(v)}
                    </Tag>
                ) : (
                    <Text type="secondary">{enToFaDigits(0)}</Text>
                ),
        },
    ];

    const renderMobileCard = (r) => (
        <Card
            key={r.operatorId}
            size="small"
            className="mb-2 rounded-xl"
            bodyStyle={{padding: 12}}
        >
            <div className="flex justify-between items-center mb-1">
                <Space>
                    <Badge
                        status={r.onShift ? "success" : "default"}
                        title={r.onShift ? "در شیفت" : "خارج از شیفت"}
                    />
                    <Text strong>{r.fullName}</Text>
                </Space>
                <Tag color={r.onShift ? "green" : undefined}>
                    {r.onShift ? "در شیفت" : "خارج از شیفت"}
                </Tag>
            </div>
            <div className="mb-1">{renderLoad(r)}</div>
            <div className="text-xs">
                <Text type="secondary">در صف این اپراتور: </Text>
                <span>{enToFaDigits(r.queuedChats ?? 0)}</span>
            </div>
            <div className="text-xs">
                <Text type="secondary">خوانده‌نشده: </Text>
                <span>{enToFaDigits(r.unreadChats ?? 0)}</span>
            </div>
        </Card>
    );

    return (
        <Card
            dir="rtl"
            className="w-full"
            title={
                <Space>
                    <TeamOutlined/>
                    <span>وضعیت اپراتورها</span>
                </Space>
            }
            extra={
                <Space size="small" wrap>
                    <Select
                        size="small"
                        value={filterMode}
                        style={{minWidth: 150}}
                        onChange={setFilterMode}
                        options={[
                            {value: "ALL", label: "همه اپراتورها"},
                            {value: "ON_SHIFT", label: "فقط در شیفت"},
                            {value: "OFF_SHIFT", label: "خارج از شیفت"},
                        ]}
                    />
                    <Tooltip title="بروزرسانی">
                        <Button
                            size="small"
                            icon={<ReloadOutlined/>}
                            onClick={() => refetch()}
                            loading={isFetching}
                        >
                            بروزرسانی
                        </Button>
                    </Tooltip>
                </Space>
            }
            bodyStyle={{padding: 12}}
        >
            <Alert
                type="info"
                showIcon
                className="mb-3"
                message="این صفحه فقط گزارش وضعیت است"
                description="داده‌ها هر ۱۵ ثانیه از سرور تازه می‌شوند. تغییر وضعیت یا جابجایی شیفت از این‌جا انجام نمی‌شود."
            />

            {isError ? (
                <Alert
                    type="error"
                    showIcon
                    message="دریافت وضعیت اپراتورها ناموفق بود"
                />
            ) : isDesktop ? (
                <Table
                    rowKey="operatorId"
                    size="small"
                    bordered
                    loading={isLoading}
                    dataSource={filteredRows}
                    columns={columns}
                    pagination={false}
                    locale={{
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="اپراتوری یافت نشد"
                            />
                        ),
                    }}
                    rowClassName={(_, idx) => (idx % 2 === 0 ? "zebra-row" : "")}
                    scroll={{x: 900}}
                />
            ) : (
                <Row gutter={[0, 0]}>
                    <Col span={24}>
                        {filteredRows.length ? (
                            filteredRows.map((r) => renderMobileCard(r))
                        ) : (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="اپراتوری یافت نشد"
                            />
                        )}
                    </Col>
                </Row>
            )}

            <style>{`
                .zebra-row td { background: var(--npe-row-alt); }
            `}</style>
        </Card>
    );
};

export default OperatorStatusManager;
